/**
 * StatusBadge Component
 * Displays status with appropriate colors following FindOp dark mode design system
 */

export type BadgeStatus =
  | "draft"
  | "pending_review"
  | "approved"
  | "scheduled"
  | "published"
  | "rejected"
  | "failed"
  | "active"
  | "inactive"
  | "warning"
  | "error"
  | "success";

interface StatusBadgeProps {
  status: BadgeStatus;
  className?: string;
}

const statusConfig: Record<
  BadgeStatus,
  { label: string; bgColor: string; textColor: string; borderColor: string }
> = {
  draft: {
    label: "Draft",
    bgColor: "bg-secondary-800",
    textColor: "text-secondary-300",
    borderColor: "border-secondary-700",
  },
  pending_review: {
    label: "Pending Review",
    bgColor: "bg-accent-500/10",
    textColor: "text-accent-400",
    borderColor: "border-accent-500/30",
  },
  approved: {
    label: "Approved",
    bgColor: "bg-success-500/10",
    textColor: "text-success-400",
    borderColor: "border-success-500/30",
  },
  scheduled: {
    label: "Scheduled",
    bgColor: "bg-accent-500/10",
    textColor: "text-accent-400",
    borderColor: "border-accent-500/30",
  },
  published: {
    label: "Published",
    bgColor: "bg-primary-500/10",
    textColor: "text-primary-400",
    borderColor: "border-primary-500/30",
  },
  rejected: {
    label: "Rejected",
    bgColor: "bg-error-500/10",
    textColor: "text-error-400",
    borderColor: "border-error-500/30",
  },
  failed: {
    label: "Failed",
    bgColor: "bg-error-500/10",
    textColor: "text-error-400",
    borderColor: "border-error-500/30",
  },
  active: {
    label: "Active",
    bgColor: "bg-success-500/10",
    textColor: "text-success-400",
    borderColor: "border-success-500/30",
  },
  inactive: {
    label: "Inactive",
    bgColor: "bg-secondary-800",
    textColor: "text-secondary-400",
    borderColor: "border-secondary-700",
  },
  warning: {
    label: "Warning",
    bgColor: "bg-warning-500/10",
    textColor: "text-warning-400",
    borderColor: "border-warning-500/30",
  },
  error: {
    label: "Error",
    bgColor: "bg-error-500/10",
    textColor: "text-error-400",
    borderColor: "border-error-500/30",
  },
  success: {
    label: "Success",
    bgColor: "bg-success-500/10",
    textColor: "text-success-400",
    borderColor: "border-success-500/30",
  },
};

export function StatusBadge({ status, className = "" }: StatusBadgeProps) {
  const config = statusConfig[status];
  return (
    <span
      className={`inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-medium ${config.bgColor} ${config.textColor} ${config.borderColor} ${className}`}
      role="status"
      aria-label={`Status: ${config.label}`}
    >
      {config.label}
    </span>
  );
}
